import React, { createContext, useCallback, useContext } from 'react';

import { useIntl } from 'react-intl-next';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { AsyncIntlProvider } from '../../common/utils/locale';
import { useMessagesForLocation } from '../../common/utils/use-messages-for-location';
import { useSentryClient } from '../../services/sentry';
import type { NavErrorBoundaryProps } from '../../ui/types';
import { useMessageFlags } from '../use-message-flags';

/**
 * NavBoundaryProvider: Handles error boundary context for navigation components
 * can be used with NavErrorBoundary or standalone to provide error handling capabilities for async operations
 *
 * @param props
 * @returns error handler function in context
 */
export function NavBoundaryProvider(props: NavErrorBoundaryProps) {
	return (
		<AsyncIntlProvider>
			<FlagsProvider>
				<InnerNavBoundaryProvider {...props} />
			</FlagsProvider>
		</AsyncIntlProvider>
	);
}

const InnerNavBoundaryProvider = ({
	children,
	disableSentry = false,
	disableAnalytics = false,
	location = 'unknown',
	onError,
	showFlags,
	sentryConfig,
}: NavErrorBoundaryProps) => {
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const flags = useMessageFlags({ showFlags });

	const { sentryInitialised, sentryClient } = useSentryClient(
		disableSentry,
		sentryConfig,
		location,
	);
	const { header: flagTitle, description: flagDescription } = useMessagesForLocation(location);

	const handleError = useCallback(
		(error: Error, errorInfo?: React.ErrorInfo) => {
			// Send events to sentry
			if (sentryInitialised && sentryClient && fg('navx-1574-enable-sentry-logs')) {
				try {
					sentryClient.captureException(error, {
						extra: {
							errorInfo,
							location,
						},
					});
				} catch (error) {
					// Log failure to send to Sentry
					fireEvent('operational.logToSentry.failed', {
						location,
						reason: error instanceof Error ? error.message : 'unknown',
					});
				}
			}

			// Fire Analytics event
			if (!disableAnalytics) {
				fireEvent('operational.errorBoundary.unhandledErrorCaught', {
					location,
					errorMessage: error.message,
					errorStack: error.stack || 'no stack trace',
				});
			}

			// Show failure flag if enabled
			flags?.showFailure?.({
				title: formatMessage(flagTitle),
				description: formatMessage(flagDescription),
			});

			// handler Callback
			onError && onError(error, { ...errorInfo });
		},
		[
			sentryInitialised,
			sentryClient,
			disableAnalytics,
			flags,
			formatMessage,
			flagTitle,
			flagDescription,
			onError,
			location,
			fireEvent,
		],
	);

	return (
		<NavErrorBoundaryContext.Provider value={{ handleError }}>
			{children}
		</NavErrorBoundaryContext.Provider>
	);
};

export function useNavBoundaryContext(): NavErrorBoundaryContextType {
	const context = useContext(NavErrorBoundaryContext);
	if (!context) {
		throw new Error('useNavErrorBoundaryContext must be used within a NavErrorBoundaryProvider');
	}
	return context;
}

const NavErrorBoundaryContext = createContext<NavErrorBoundaryContextType | null>(null);

type NavErrorBoundaryContextType = {
	handleError: (error: Error, errorInfo?: React.ErrorInfo) => void;
};
